import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect, useMemo } from 'react';
import { FaArrowLeft, FaShare } from 'react-icons/fa6';
import { saveAs } from 'file-saver';
import { decompress } from 'compress-json';
import { VersionPicker } from './VersionPicker';
import { Text } from '~/components/ui/text';
import { Metadata } from '~/components/layout/Metadata';
import { Box, HStack, Stack, styled } from 'styled-system/jsx';

import { ChartViewer, type ChartViewerHandles } from '~/components/ChartViewer';
import { Button } from '~/components/ui/button';
import { RadioButtonGroup } from '~/components/ui/radio-button-group';
import { Link } from '~/components/ui/link';
import { getAlbumArtPublicPath } from '~/utils/assets';
import { Checkbox } from '~/components/ui/checkbox';
import { calculateFeverLength } from '~/utils/feverSection';
import { getLocalizedSongTitle } from '~/pages/index/components/songDisplay';

import type { PageData } from '~/pages/@id/+data';
import type { PreProcessedChartFileSoA } from '~/types';

export function ChartDisplayPage({ chartInfo, chartData, versions }: PageData) {
  const { t, i18n } = useTranslation();
  const chartViewerRef = useRef<ChartViewerHandles>(null);
  const locale = i18n.resolvedLanguage ?? i18n.language;

  const getInitialDifficulty = (): 1 | 2 | 3 | 4 => {
    const difficultySearchParam = new URLSearchParams(window.location.search).get('difficulty');
    if (difficultySearchParam && ['1', '2', '3', '4'].includes(difficultySearchParam)) {
      return parseInt(difficultySearchParam, 10) as 1 | 2 | 3 | 4;
    }
    return 4;
  };

  const [difficulty, setDifficulty] = useState<1 | 2 | 3 | 4>(1);
  const [showJacket, setShowJacket] = useState(true);
  const [shareButtonText, setShareButtonText] = useState(t('share', 'Share'));

  const decompressedChartData: Record<1 | 2 | 3 | 4, PreProcessedChartFileSoA> = useMemo(
    () => decompress(chartData),
    [chartData]
  );

  const chart = decompressedChartData[difficulty];
  const title = getLocalizedSongTitle(chartInfo, locale);
  const secondaryTitle =
    locale.startsWith('en') && chartInfo.englishName && chartInfo.englishName !== chartInfo.title
      ? chartInfo.title
      : undefined;
  const localizedUnitName =
    locale.startsWith('en') && chartInfo.unitEnglishName
      ? chartInfo.unitEnglishName
      : chartInfo.unitName;
  const localizedCenterName =
    locale.startsWith('en') && chartInfo.centerCharacterEnglishName
      ? chartInfo.centerCharacterEnglishName
      : chartInfo.centerCharacterName;
  const characterNames = chartInfo.characterNames ?? [];
  const characterEnglishNames = chartInfo.characterEnglishNames ?? [];
  const localizedCharacterNames =
    locale.startsWith('en') && characterEnglishNames.length > 0
      ? characterEnglishNames
      : characterNames;

  const difficultyLabels = {
    1: 'NORMAL',
    2: 'HARD',
    3: 'EXPERT',
    4: 'MASTER'
  };

  const options = ([1, 2, 3, 4] as const).map((diffKey) => {
    const difficultyValue =
      chartInfo.difficulty[
        difficultyLabels[diffKey].toLowerCase() as keyof typeof chartInfo.difficulty
      ];
    const maxComboValue =
      chartInfo.maxCombo[
        difficultyLabels[diffKey].toLowerCase() as keyof typeof chartInfo.maxCombo
      ];
    return {
      label: difficultyLabels[diffKey],
      value: diffKey,
      difficulty: difficultyValue,
      maxCombo: maxComboValue
    };
  });

  const currentDifficultyLabel = difficultyLabels[difficulty];
  const fileNamePrefix = `${title} - ${currentDifficultyLabel}`;

  const baseDescription = localizedUnitName ?? chartInfo.description;
  const difficultyDetailsString = `| Normal: ${chartInfo.difficulty.normal} | Hard: ${chartInfo.difficulty.hard} | Expert: ${chartInfo.difficulty.expert} | Master: ${chartInfo.difficulty.master} |`;
  const pageDescription = `${baseDescription}\n${difficultyDetailsString}`;

  let feverLengthDetails = '';
  const feverData = calculateFeverLength(
    chartInfo.feverSection,
    chartInfo.sectionTimes,
    chartInfo.duration
  );

  if (feverData) {
    const totalSeconds = feverData.lengthMs / 1000;
    feverLengthDetails = `${t('fever_length', 'Fever Length')}: ${totalSeconds.toFixed(2)} ${t('seconds')}`;
  }

  const descriptionParts: string[] = [];
  if (localizedUnitName) {
    descriptionParts.push(localizedUnitName);
  } else if (chartInfo.description) {
    descriptionParts.push(chartInfo.description);
  }

  if (localizedCenterName) {
    descriptionParts.push(`${t('song_details.center')}: ${localizedCenterName}`);
  }

  if (localizedCharacterNames.length > 0) {
    descriptionParts.push(`${t('song_details.characters')}: ${localizedCharacterNames.join(', ')}`);
  }

  if (chart && chart[8] && chart[8].length > 0) {
    const bpmValues = chart[8].map((e) => e.Bpm).join(',');
    descriptionParts.push(`BPM: ${bpmValues}`);
  }

  const totalDurationSeconds = chartInfo.duration / 1000;
  const durationMinutes = Math.floor(totalDurationSeconds / 60);
  const durationSeconds = Math.floor(totalDurationSeconds % 60);
  descriptionParts.push(
    `${durationMinutes}:${durationSeconds.toString().padStart(2, '0')} (${totalDurationSeconds.toFixed(2)} ${t('seconds')})`
  );

  if (feverLengthDetails) {
    descriptionParts.push(feverLengthDetails);
  }
  const chartDisplayString = descriptionParts.join(' | ');

  const handleSaveImage = () => {
    chartViewerRef.current?.getCanvasBlob(
      (blob) => {
        if (blob) {
          saveAs(blob, `${fileNamePrefix}_chart.png`);
        } else {
        }
      },
      'image/png',
      1,
      showJacket
    );
  };

  const handleOpenInNewTab = () => {
    chartViewerRef.current?.getCanvasBlob(
      (blob) => {
        if (blob) {
          const objectUrl = URL.createObjectURL(blob);
          const newTab = window.open(objectUrl, '_blank');
          if (newTab) {
            newTab.addEventListener('pagehide', () => URL.revokeObjectURL(objectUrl), {
              once: true
            });
          } else {
            URL.revokeObjectURL(objectUrl);
          }
        } else {
        }
      },
      'image/png',
      1,
      showJacket
    );
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setShareButtonText(t('copied', 'Copied!'));
        setTimeout(() => {
          setShareButtonText(t('share', 'Share'));
        }, 2000);
      })
      .catch((err) => {
        console.error('Failed to copy URL:', err);
      });
  };

  useEffect(() => {
    setDifficulty(getInitialDifficulty());
  }, []);

  return (
    <>
      <Metadata
        title={`${chartInfo.title} | ${t('site_name')}`}
        description={pageDescription}
        imageUrl={chartInfo?.id ? getAlbumArtPublicPath(chartInfo.id) : undefined}
      />
      <Stack alignItems="center" w="full" _print={{ display: 'none' }}>
        <Box>
          <Link href="/">
            <FaArrowLeft />
            {t('back_to_list')}
          </Link>
        </Box>
        <Text textAlign="center" fontSize="3xl" fontWeight="bold">
          {title}
        </Text>
        {secondaryTitle ? (
          <Text hideBelow="md" color="fg.muted" textAlign="center">
            {secondaryTitle}
          </Text>
        ) : null}
        <Box w="48" h="48" my="4">
          <styled.img
            src={getAlbumArtPublicPath(chartInfo.id)}
            alt={`${title} album art`}
            objectFit="contain"
            w="full"
            h="full"
          />
        </Box>
        <Text textAlign="center">{chartDisplayString}</Text>
        <RadioButtonGroup.Root
          size="xl"
          value={String(difficulty)}
          onValueChange={(v) => setDifficulty(Number(v.value) as 1 | 2 | 3 | 4)}
          justifyContent="center"
          alignItems="center"
        >
          {options.map((option) => (
            <RadioButtonGroup.Item
              key={option.value}
              value={option.value + ''}
              borderColor={`difficulty.${option.label.toLowerCase()}.fg`}
              borderWidth="1px"
              height="fit-content"
              p="2"
              color={`difficulty.${option.label.toLowerCase()}.fg`}
              background="unset"
              borderStyle="solid"
              _checked={{
                backgroundColor: `difficulty.${option.label.toLowerCase()}.bg`,
                color: 'fg.default',
                borderColor: `difficulty.${option.label.toLowerCase()}.bg`
              }}
            >
              <RadioButtonGroup.ItemControl />
              <RadioButtonGroup.ItemText>
                <Stack gap="0" alignItems="center" textAlign="center">
                  <Stack gap="0.5" direction="row" alignItems="baseline">
                    <Text fontSize="xs">Lv.</Text>
                    <Text fontSize="xl" fontWeight="bold" lineHeight="1">
                      {option.difficulty}
                    </Text>
                    <Text fontSize="xs" lineHeight="1">
                      ({option.maxCombo})
                    </Text>
                  </Stack>
                  <Text fontSize="sm" lineHeight="1.2">
                    {option.label}
                  </Text>
                </Stack>
              </RadioButtonGroup.ItemText>
              <RadioButtonGroup.ItemHiddenInput />
            </RadioButtonGroup.Item>
          ))}
        </RadioButtonGroup.Root>
        {versions.length > 0 && <VersionPicker id={chartInfo.id.toString()} versions={versions} />}
        <HStack gap="4" justifyContent="center" mt="4">
          <Checkbox
            checked={showJacket}
            onCheckedChange={(e) => setShowJacket(e.checked as boolean)}
          >
            {t('show_jacket')}
          </Checkbox>
          <Button onClick={handleSaveImage} disabled={!chart}>
            {t('save_image')}
          </Button>
          <Button onClick={handleOpenInNewTab} variant="outline" disabled={!chart}>
            {t('open_image_new_tab')}
          </Button>
          <Button onClick={handleShare} variant="ghost">
            <FaShare />
            {shareButtonText}
          </Button>
        </HStack>
        <Box w="full" mt="4" overflowX="auto">
          <Box pointerEvents="none">
            <ChartViewer
              ref={chartViewerRef}
              chart={chart}
              feverSection={chartInfo?.feverSection}
              sectionTimes={chartInfo?.sectionTimes}
              imageUrl={
                showJacket && chartInfo?.id ? getAlbumArtPublicPath(chartInfo.id) : undefined
              }
            />
          </Box>
        </Box>
      </Stack>
    </>
  );
}
